import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Eye, EyeOff, Loader2, ImageOff, Film } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import MediaThumb from "@/components/admin/MediaThumb";
import ProjectEditor from "@/pages/admin/ProjectEditor";
import { api, formatApiError } from "@/lib/api";

// Admin: manage real Kensmarc projects shown on /our-work.
export default function ProjectsPanel() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null closed, {} new, project edit
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/projects");
      setProjects(data);
    } catch (err) {
      toast.error(formatApiError(err, "Could not load projects"));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const togglePublished = async (p) => {
    try {
      const { data } = await api.patch(`/admin/projects/${p.id}`, { published: !p.published });
      setProjects((list) => list.map((x) => (x.id === p.id ? data : x)));
      toast.success(data.published ? "Published" : "Hidden from Our Work");
    } catch (err) {
      toast.error(formatApiError(err, "Update failed"));
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/admin/projects/${deleting.id}`);
      setProjects((list) => list.filter((x) => x.id !== deleting.id));
      toast.success("Project deleted");
    } catch (err) {
      toast.error(formatApiError(err, "Delete failed"));
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div data-testid="projects-panel">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-neutral-400">Only real Kensmarc Electric job photos and videos belong here. Published projects appear on <span className="text-neutral-200">Our Work</span>.</p>
        <button onClick={() => setEditing({})} className="inline-flex items-center gap-2 rounded-lg bg-amber-400 px-4 py-2 text-sm font-bold text-[#0A0A0C] hover:bg-amber-300" data-testid="project-add-btn">
          <Plus className="h-4 w-4" /> Add project
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-amber-400" /></div>
      ) : projects.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[#2a2a34] bg-[#101017] p-16 text-center" data-testid="projects-empty">
          <ImageOff className="mx-auto h-10 w-10 text-neutral-600" />
          <p className="mt-4 text-neutral-400">No projects yet. Add your first real job with photos or video.</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-[#20202a]">
          <div className="divide-y divide-[#1a1a22]">
            {projects.map((p) => {
              const cover = p.media?.[0];
              const videos = p.media?.filter((m) => m.type === "video").length || 0;
              return (
                <div key={p.id} className="flex items-center gap-4 p-4" data-testid={`project-row-${p.id.slice(0, 6)}`}>
                  {cover ? <MediaThumb item={cover} className="h-16 w-20" /> : <div className="flex h-16 w-20 shrink-0 items-center justify-center rounded-lg border border-[#2a2a34] text-neutral-600"><ImageOff className="h-5 w-5" /></div>}
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="truncate font-semibold text-white">{p.title}</p>
                      <span className={`rounded-full border px-2 py-0.5 text-[0.65rem] font-bold uppercase ${p.published ? "border-green-400/30 bg-green-400/15 text-green-200" : "border-neutral-500/30 bg-neutral-500/15 text-neutral-300"}`}>{p.published ? "Published" : "Hidden"}</span>
                    </div>
                    <p className="mt-0.5 flex items-center gap-2 text-xs text-neutral-400">
                      {p.category} · {p.media?.length || 0} media{videos > 0 && <span className="inline-flex items-center gap-1"><Film className="h-3 w-3" /> {videos} video</span>}
                    </p>
                  </div>
                  <div className="flex shrink-0 gap-2">
                    <RowBtn onClick={() => togglePublished(p)} label={p.published ? "Hide" : "Publish"} testId={`project-toggle-${p.id.slice(0, 6)}`}>{p.published ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</RowBtn>
                    <RowBtn onClick={() => setEditing(p)} label="Edit" testId={`project-edit-${p.id.slice(0, 6)}`}><Pencil className="h-4 w-4" /></RowBtn>
                    <RowBtn onClick={() => setDeleting(p)} label="Delete" danger testId={`project-delete-${p.id.slice(0, 6)}`}><Trash2 className="h-4 w-4" /></RowBtn>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <ProjectEditor
        open={editing !== null}
        project={editing && editing.id ? editing : null}
        onClose={() => setEditing(null)}
        onSaved={(saved) => setProjects((list) => (list.some((x) => x.id === saved.id) ? list.map((x) => (x.id === saved.id ? saved : x)) : [saved, ...list]))}
      />

      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent className="border-[#26262f] bg-[#0f0f14] text-white" data-testid="project-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{deleting?.title}"?</AlertDialogTitle>
            <AlertDialogDescription className="text-neutral-400">This removes the project from Our Work. Uploaded files stay in storage.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#2a2a34] bg-transparent text-white hover:bg-white/5" data-testid="project-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-red-500 text-white hover:bg-red-400" data-testid="project-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function RowBtn({ onClick, label, danger, testId, children }) {
  return (
    <button onClick={onClick} title={label} aria-label={label} data-testid={testId}
      className={`flex h-9 w-9 items-center justify-center rounded-lg border transition-colors ${danger ? "border-red-400/40 text-red-300 hover:bg-red-400/10" : "border-[#2a2a34] text-neutral-200 hover:border-amber-400/60"}`}>
      {children}
    </button>
  );
}
